import React from 'react';
import dayjs from 'dayjs';
import { Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import type { WorkProgressBagDiffMawbBagSummaryRow } from '../../graphql/queries/getWorkProgressBagDiffMawbBagSummaries';
import { BIG_BAG_TYPE_WITH_MAWB, type BigBagType } from '../bagType';
import { BigBagDrilldownTitleSource } from '../bigBagDrilldownTitleSource';
import { BigBagParcelDiffRateCell, CLICKABLE_UNDERLINE, bigBagDiffRateTone, bigBagStatusKey } from '../bigBagHelpers';

/**
 * 大包差异 tab 第三级表：MAWB 下的单个大包行。
 * 列：大包号(可下钻) / 扫描日期(client_carton.scan_time) / 预期=1 / 入库=0/1 / 差异 / 差异率 / 状态。
 *
 * 与下钻 drawer 同源：`scanTime` 来自 client_carton.scan_time（在 Application 层 merge）。
 * ETL 已为未入库行写入 work_progress_bag_detail（is_inbound=0、pre_pickup_hub_id 填真值），后端 SQL 直接返回，前端无需补占位。
 */
export interface MawbBagDiffSummaryTableProps {
  customerId: number;
  customerName: string;
  mawb: string;
  rows: WorkProgressBagDiffMawbBagSummaryRow[];
  loading: boolean;
  onDrilldown: (
    ctx: {
      customerId: number;
      customerName: string;
      bagType: BigBagType;
      mawb?: string | null;
      bigBagNo?: string | null;
      detailArrivalDate?: string | null;
    },
    opts: {
      titleSource: BigBagDrilldownTitleSource;
      nextIsInbound?: true | null;
    },
  ) => void;
}

const MawbBagDiffSummaryTable: React.FC<MawbBagDiffSummaryTableProps> = ({
  customerId,
  customerName,
  mawb,
  rows,
  loading,
  onDrilldown,
}) => {
  const { t } = useTranslation();
  const statusLabel = (key: ReturnType<typeof bigBagStatusKey>) => t(`wms.progress.bigBag.status.${key}`);

  return (
    <Table
      rowKey={(r) => r.bagNumber}
      size="middle"
      pagination={false}
      loading={loading}
      dataSource={rows}
      scroll={{ x: 1080, y: 360 }}
      columns={[
        {
          title: t('wms.progress.bigBag.columns.bagNumber'),
          dataIndex: 'bagNumber',
          key: 'bagNumber',
          width: 220,
          render: (v: string, r: WorkProgressBagDiffMawbBagSummaryRow) => {
            const scanDay = r.scanTime ? dayjs.utc(r.scanTime).local().format('YYYY-MM-DD') : null;
            return (
              <span
                className={`text-violet-600 tabular-nums break-all ${CLICKABLE_UNDERLINE}`}
                onClick={() =>
                  onDrilldown(
                    {
                      customerId,
                      customerName,
                      bagType: BIG_BAG_TYPE_WITH_MAWB,
                      mawb,
                      bigBagNo: v,
                      detailArrivalDate: scanDay,
                    },
                    {
                      titleSource: r.isInbound
                        ? BigBagDrilldownTitleSource.MawbBagDetailInbound
                        : BigBagDrilldownTitleSource.MawbBagDetailNotInbound,
                      nextIsInbound: r.isInbound ? true : null,
                    },
                  )
                }
              >
                {v || '-'}
              </span>
            );
          },
        },
        {
          title: t('wms.progress.bigBag.columns.bagScanDate'),
          key: 'bagScanDate',
          width: 160,
          render: (_: unknown, row: WorkProgressBagDiffMawbBagSummaryRow) => {
            const scanDay = row.scanTime ? dayjs.utc(row.scanTime).local().format('YYYY-MM-DD') : '';
            return <span className="tabular-nums text-gray-700">{scanDay || '-'}</span>;
          },
        },
        {
          title: t('wms.progress.bigBag.columns.expectedBagCount'),
          key: 'expectedBag',
          width: 120,
          render: () => <span className="tabular-nums">1</span>,
        },
        {
          title: t('wms.progress.bigBag.columns.inboundBagCount'),
          dataIndex: 'isInbound',
          key: 'inboundBag',
          width: 120,
          render: (isInbound: boolean) => (
            <span className={`tabular-nums ${isInbound ? '' : 'text-red-600 font-semibold'}`}>{isInbound ? 1 : 0}</span>
          ),
        },
        {
          title: t('wms.progress.bigBag.columns.bagDiff'),
          key: 'bagDiff',
          width: 120,
          render: (_: unknown, row: WorkProgressBagDiffMawbBagSummaryRow) => {
            const diff = row.isInbound ? 0 : 1;
            return <span className={`tabular-nums ${diff > 0 ? 'text-red-600 font-semibold' : ''}`}>{diff}</span>;
          },
        },
        {
          title: t('wms.progress.bigBag.columns.bagDiffRate'),
          key: 'bagDiffRate',
          width: 220,
          render: (_: unknown, row: WorkProgressBagDiffMawbBagSummaryRow) => (
            <BigBagParcelDiffRateCell rate={row.isInbound ? 0 : 1} />
          ),
        },
        {
          title: t('wms.progress.bigBag.columns.status'),
          key: 'status',
          width: 100,
          render: (_: unknown, row: WorkProgressBagDiffMawbBagSummaryRow) => {
            const rate = row.isInbound ? 0 : 1;
            const { tagColor } = bigBagDiffRateTone(rate);
            return <Tag color={tagColor}>{statusLabel(bigBagStatusKey(rate))}</Tag>;
          },
        },
      ]}
      className="[&_.ant-table-container]:bg-transparent [&_.ant-table-content]:bg-transparent [&_.ant-table-tbody>tr>td]:bg-transparent [&_.ant-table-thead>tr>th]:!bg-[#fafafa] [&_.ant-table-sticky-holder]:!bg-[#fafafa]"
    />
  );
};

export default MawbBagDiffSummaryTable;
